import React, { useState, useMemo } from 'react';
import { Box, Button, SpaceBetween, StatusIndicator, Checkbox, Container } from '@amzn/awsui-components-react';
import { MDAWorksheetSelectorFormState } from './MDAWorksheetSelector';
import { WizardStepComponent, RecordMode } from './MDAWorksheetSteps';
import { TPERowSelectableReactTable } from 'src/components/shared/TPERowSelectableReactTable';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import 'src/assets/styles/react-table.scss';
import { CellIdentifier, RowData, SelectableCell, useSourceLineExcelData } from './hooks/useSourceLineExcelData';

// Step 3: MDA Worksheet Data Source Line Selection Table Component
export const SourceLineSelectionTable: WizardStepComponent<MDAWorksheetSelectorFormState> = (props) => {
    // Get pre-signed URL from previous step
    const preSignedUrl = props.initialData?.worksheetDatasourceS3PresignUrl;
    const initialSelections = props.initialData?.selectedSourceLineIdentifiers;
    const [isSaving, setIsSaving] = useState(false);
    // Use the custom hook to fetch and parse Excel data
    const { excelData, functionHeaders, loading, error } = useSourceLineExcelData(preSignedUrl, initialSelections);

    // Track selection changes to force re-render
    const [lastSelectionChangeTime, setLastSelectionChangeTime] = useState(new Date());

    // Determine if multi-selection is allowed based on recordMode
    const allowMultiSelection = props.recordMode === RecordMode.NEW_RECORD;

    // Helper function to check if all cells in a column are selected
    const isColumnFullySelected = (functionName: string): boolean => {
        if (excelData.length === 0) return false;
        return excelData.every((row) => {
            const cell = row[functionName] as SelectableCell;
            return cell && cell.isSelected;
        });
    };

    // Helper function to clear all selections
    const clearAllSelections = () => {
        excelData.forEach((row) => {
            functionHeaders.forEach((functionName) => {
                const cell = row[functionName] as SelectableCell;
                if (cell) {
                    cell.isSelected = false;
                }
            });
        });
    };

    // Handle row-level selection when the row checkbox (first column) is clicked
    const handleRowClicked = (isChecked: boolean, row: RowData, rowIndex: number) => {
        functionHeaders.forEach((functionName) => {
            const cell = row[functionName] as SelectableCell;
            if (cell) {
                cell.isSelected = isChecked;
            }
        });
        setLastSelectionChangeTime(new Date());
    };


    // Create column definitions dynamically based on Excel headers
    const columnDefinitions = useMemo(() => {
        const columns: any[] = [
            {
                Header: 'Company Code',
                accessor: 'companyCode',
                sortable: false,
                Cell: (cellProps: any) => {
                    const companyCode = cellProps.value;

                    return (
                        <div className="cell-text" style={{ textAlign: 'right' }}>
                            {companyCode}
                        </div>
                    );
                },
            },
        ];

        // Add dynamic function columns with column header checkbox for multi-selection mode
        functionHeaders.forEach((functionName) => {
            columns.push({
                Header: allowMultiSelection ? (
                    <div className="cell-text selectable-cell">
                        <Checkbox
                            checked={isColumnFullySelected(functionName)}
                            onChange={({ detail }) => {
                                // If checking the column, select all cells in this column
                                if (detail.checked) {
                                    excelData.forEach((row) => {
                                        const cell = row[functionName] as SelectableCell;
                                        if (cell) {
                                            cell.isSelected = true;
                                        }
                                    });
                                } else {
                                    // If unchecking, deselect all cells in this column
                                    excelData.forEach((row) => {
                                        const cell = row[functionName] as SelectableCell;
                                        if (cell) {
                                            cell.isSelected = false;
                                        }
                                    });
                                }
                                setLastSelectionChangeTime(new Date());
                            }}>
                            <span style={{ textAlign: 'right', display: 'inline-block', width: '100%' }}>
                                {functionName}
                            </span>
                        </Checkbox>
                    </div>
                ) : functionName,
                accessor: functionName,
                sortable: false,
                Cell: (cellProps: any) => {
                    const selectableCell = cellProps.value as SelectableCell;
                    if (!selectableCell) {
                        return (
                            <div className="cell-text" style={{ textAlign: 'right' }}>
                                -
                            </div>
                        );
                    }

                    return (
                        <div className="cell-text selectable-cell">
                            <Checkbox
                                checked={selectableCell.isSelected}
                                onChange={({ detail }) => {
                                    // If single selection mode and checking a cell, clear all other selections first
                                    if (!allowMultiSelection && detail.checked) {
                                        clearAllSelections();
                                    }
                                    selectableCell.isSelected = detail.checked;
                                    // Force re-render
                                    setLastSelectionChangeTime(new Date());
                                }}>
                                <span style={{ textAlign: 'right', display: 'inline-block', width: '100%' }}>
                                    {selectableCell.value}
                                </span>
                            </Checkbox>
                        </div>
                    );
                },
            });
        });

        return columns;
    }, [functionHeaders, lastSelectionChangeTime]);

    const handleSave = () => {
        // Collect all selected cells
        const selectedCells: CellIdentifier[] = [];

        excelData.forEach((row) => {
            functionHeaders.forEach((functionName) => {
                const cell = row[functionName] as SelectableCell;
                if (cell && cell.isSelected) {
                    selectedCells.push({
                        xcellId: cell.xcellId,
                        companyCode: row.companyCode,
                        function: functionName,
                        value: cell.value,
                    });
                }
            });
        });

        const stepData: Partial<MDAWorksheetSelectorFormState> = {
            selectedSourceLineIdentifiers: selectedCells,
        };
        setIsSaving(true);
        props.onSave(stepData);
        setIsSaving(false);
    };

    // Calculate count of selected cells
    const selectedCount = useMemo(() => {
        let count = 0;
        excelData.forEach((row) => {
            functionHeaders.forEach((functionName) => {
                const cell = row[functionName] as SelectableCell;
                if (cell && cell.isSelected) {
                    count++;
                }
            });
        });
        return count;
    }, [excelData, functionHeaders, lastSelectionChangeTime]);

    // Empty state message
    const emptyMessage = loading ? (
        <Box textAlign="center" padding="l">
            <StatusIndicator type="loading">
                Loading Data Source File...
            </StatusIndicator>
        </Box>
    ) : error ? (
        <Box textAlign="center" padding="l">
            <StatusIndicator type="error">Error loading data: {error}</StatusIndicator>
        </Box>
    ) : !preSignedUrl ? (
        <Box textAlign="center" padding="l">
            <StatusIndicator type="warning">
                No data source selected. Please go back and select a data source.
            </StatusIndicator>
        </Box>
    ) : (
        <Box textAlign="center" padding="l">
            <StatusIndicator type="info">No data available</StatusIndicator>
        </Box>
    );

    return (
        <Container
            data-class='dataKeyContentContainer'
            header={
                <SpaceBetween direction='horizontal' size='l'>
                    <h3>Select output from source line </h3>
                    {selectedCount > 0 && (
                        <Box variant="p" color="text-status-info">
                            {selectedCount} cell(s) selected
                        </Box>
                    )}
                </SpaceBetween>
            }
            footer={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={props.onCancel}>
                        Cancel
                    </Button>
                    <Button onClick={props.onBack}>Back</Button>
                    <Button variant="primary" 
                        onClick={handleSave} 
                        disabled={loading || selectedCount === 0} 
                        loading={loading || isSaving}>
                        Save Data Source
                    </Button>
                </SpaceBetween>
            }>
            {loading || error || !preSignedUrl || excelData.length === 0 ? (
                emptyMessage
            ) : allowMultiSelection ? (
                <TPERowSelectableReactTable
                    data={excelData}
                    columnDefinitions={columnDefinitions}
                    className="nonGridTable"
                    sortable={false}
                    showFilters={false}
                    showHeader={true}
                    isSearching={loading}
                    isSearchingMessage="Loading source lines..."
                    emptyMessage="No source lines available"
                    allowMultipleRowSelection={true}
                    allowGlobalSelection={true}
                    highlightSelected={false}
                    onRowClicked={handleRowClicked}
                />
            ) : (
                <TPEReactTable
                    data={excelData}
                    columnDefinitions={columnDefinitions}
                    className="nonGridTable"
                    sortable={false}
                    showFilters={false}
                    showHeader={true}
                    isSearching={loading}
                    isSearchingMessage="Loading source lines..."
                    emptyMessage="No source lines available"
                />
            )}
        </Container>
    );
};
